import { EE_HOLIDAYS_2026, holidayOn } from '@progear/finance-core';
import { cn } from '@/lib/utils';
import { formatEur, formatEurCompact, isoWeek, pickSum } from '@/lib/cashFlow';
import { useCalendar } from './context';
import { DayEntries } from './DayEntries';
import { DayCell } from './DayCell';
import {
  buildCells,
  isoDay,
  MODE_BADGE,
  modeBadgeCls,
  MONTHS,
  pad2,
  quartersBetween,
  ROMAN,
  WEEKDAYS,
  weeksBetween,
} from './model';

/**
 * The four granularity layouts (Kuu / Kvartal / Nädal / Päev) the calendar can
 * render, plus the shared sticky month header. Each reads everything from the
 * calendar context and reuses the same DayCell / CalendarChip, so a day looks and
 * behaves identically regardless of which lens you are in. No layout does money
 * maths — per-period figures come from the model's finance-core aggregates.
 */

/** Sticky per-month header: name + "jooksev kuu" tag, mode badge, muutus, kuu lõpp. */
function MonthHeader({ y, m0 }: { y: number; m0: number }) {
  const { mode, today, model, accrual } = useCalendar();
  const key = `${y}-${pad2(m0 + 1)}`;
  const mr = model.monthRollups.get(key);
  const isCurrent = key === today.slice(0, 7);
  const net = mr ? pickSum(mr.net, mode) : null;

  return (
    <div className="sticky top-0 z-10 mb-1 flex flex-wrap items-center justify-between gap-2 rounded-md border border-border bg-card/95 px-2 py-1 backdrop-blur">
      <h3 className={cn('flex items-center gap-2 text-sm font-semibold', isCurrent ? 'text-primary' : 'text-foreground')}>
        {MONTHS[m0]} {y}
        {isCurrent && (
          <span className="rounded bg-primary/15 px-1.5 py-0.5 text-[10px] font-medium text-primary">jooksev kuu</span>
        )}
      </h3>
      {mr && (
        <div className="flex items-center gap-3 text-[10px]">
          <span className={cn('rounded border px-1 py-0.5 text-[9px] font-semibold leading-none', modeBadgeCls(mode))}>
            {MODE_BADGE[mode]}
          </span>
          <span className="flex items-baseline gap-1">
            <span className="uppercase tracking-wide text-muted-foreground/70">muutus</span>
            <span
              className={cn(
                'tabular-nums text-[11px] font-medium',
                (net as number) >= 0 ? 'text-[hsl(var(--income))]' : 'text-[hsl(var(--cost))]',
              )}
            >
              {(net as number) >= 0 ? '+' : '−'}
              {formatEur(Math.abs(net as number))}
            </span>
          </span>
          {/* "Kuu lõpp" is the running BANK balance — a cash truth. Under the
              accrual basis it cannot follow from the document-dated grid, so it
              hides (same rule as the table's saldo rows / the footer strip). */}
          {!accrual && (
            <span className="flex items-baseline gap-1 border-l border-border pl-3">
              <span className="uppercase tracking-wide text-muted-foreground/70">kuu lõpp</span>
              <span
                className={cn(
                  'tabular-nums text-[11px] font-semibold',
                  mr.closingBalance < 0 ? 'text-[hsl(var(--cost))]' : 'text-foreground/80',
                )}
              >
                {formatEur(mr.closingBalance)}
              </span>
            </span>
          )}
        </div>
      )}
    </div>
  );
}

/** The 7-column Monday-first grid of a single month (weekday row + day cells). */
function MonthGridSection({ y, m0 }: { y: number; m0: number }) {
  return (
    <div className="grid grid-cols-7 gap-px overflow-hidden rounded-lg border border-border bg-border">
      {WEEKDAYS.map((w) => (
        <div
          key={w}
          className="bg-card px-2 py-1 text-center text-[10px] font-medium uppercase tracking-wide text-muted-foreground"
        >
          {w}
        </div>
      ))}
      {buildCells(y, m0).map((d, i) => (
        <DayCell key={d === null ? `b${i}` : isoDay(y, m0, d)} date={d === null ? null : isoDay(y, m0, d)} />
      ))}
    </div>
  );
}

/** Kuu — every month the window spans, each a sticky header + a month grid. */
export function MonthGrid() {
  const { model } = useCalendar();
  return (
    <>
      {model.months.map((m) => (
        <section key={`${m.y}-${m.m0}`} className="mb-5">
          <MonthHeader y={m.y} m0={m.m0} />
          <MonthGridSection y={m.y} m0={m.m0} />
        </section>
      ))}
    </>
  );
}

/** Kvartal — months grouped under a Roman-numeral quarter divider (net via finance-core). */
export function QuarterGroups() {
  const { mode, model } = useCalendar();
  const groups = quartersBetween(model.months);
  return (
    <>
      {groups.map((g) => {
        const qr = model.quarterRollups.get(`${g.y}-Q${g.q + 1}`);
        const net = qr ? pickSum(qr.net, mode) : null;
        return (
          <section key={`${g.y}-${g.q}`} className="mb-6">
            <div className="mb-2 flex items-center justify-between border-b border-border pb-1">
              <h2 className="text-sm font-bold uppercase tracking-wide text-foreground">
                {ROMAN[g.q]} kvartal {g.y}
              </h2>
              {qr && (
                <span
                  className={cn(
                    'tabular-nums text-xs font-semibold',
                    (net as number) >= 0 ? 'text-[hsl(var(--income))]' : 'text-[hsl(var(--cost))]',
                  )}
                >
                  {(net as number) >= 0 ? '+' : '−'}
                  {formatEur(Math.abs(net as number))}
                </span>
              )}
            </div>
            <div className="space-y-4">
              {g.months.map((m) => (
                <div key={`${m.y}-${m.m0}`}>
                  <MonthHeader y={m.y} m0={m.m0} />
                  <MonthGridSection y={m.y} m0={m.m0} />
                </div>
              ))}
            </div>
          </section>
        );
      })}
    </>
  );
}

/** Nädal — ISO weeks as rows with a leading week-number rail, day cells reused. */
export function WeekMatrix() {
  const { model, today } = useCalendar();
  const weeks = weeksBetween(model.firstDate, model.lastDate);
  return (
    <div>
      <div className="mb-1 flex gap-px">
        <div className="w-9 shrink-0" />
        <div className="grid flex-1 grid-cols-7 gap-px">
          {WEEKDAYS.map((w) => (
            <div
              key={w}
              className="rounded bg-card px-2 py-1 text-center text-[10px] font-medium uppercase tracking-wide text-muted-foreground"
            >
              {w}
            </div>
          ))}
        </div>
      </div>
      {weeks.map((wk) => {
        const firstOfMonth = wk.find((d) => d.endsWith('-01') && d >= model.firstDate && d <= model.lastDate);
        const ref = wk.find((d) => model.rollupByDate.has(d)) ?? wk[0];
        const weekNo = isoWeek(ref);
        const todayInWeek = wk.includes(today);
        return (
          <div key={wk[0]}>
            {firstOfMonth && (
              <div className="mt-3">
                <MonthHeader y={Number(firstOfMonth.slice(0, 4))} m0={Number(firstOfMonth.slice(5, 7)) - 1} />
              </div>
            )}
            <div className="mb-px flex gap-px">
              <div
                title={`Nädal ${weekNo}`}
                className={cn(
                  'flex w-9 shrink-0 flex-col items-center justify-center rounded-l-lg border border-r-0 border-border text-[10px] leading-tight',
                  todayInWeek ? 'bg-primary/15 font-semibold text-primary' : 'bg-card/60 text-muted-foreground',
                )}
              >
                <span className="text-[8px] uppercase tracking-wide opacity-70">näd</span>
                {weekNo}
              </div>
              <div className="grid flex-1 grid-cols-7 gap-px overflow-hidden rounded-r-lg border border-border bg-border">
                {wk.map((d) => (
                  <DayCell key={d} date={d} />
                ))}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

/** A single agenda row: date · holiday · chips · day net + opening→closing. */
function AgendaRow({ date }: { date: string }) {
  const ctx = useCalendar();
  const { mode, today, model, arrangeDay, overDate, accrual } = ctx;
  const m0 = Number(date.slice(5, 7)) - 1;
  const day = Number(date.slice(8, 10));
  // DayAgenda only lists in-window dates (an entry resolves there, or a holiday
  // inside [first,last]), so the rollup + opening always exist — no defensive branch.
  const rollup = model.rollupByDate.get(date)!;
  const raw = model.entriesByDate.get(date) ?? [];
  const dayEntries = arrangeDay(raw);
  const isToday = date === today;
  const future = date > today;
  const hol = holidayOn(date)?.name;
  const net = pickSum(rollup.net, mode);
  const opening = model.openingByDate.get(date)!;
  const closing = rollup.runningBalance;

  return (
    <div
      data-date={date}
      data-now={isToday ? '1' : undefined}
      onDragOver={(ev) => ctx.dayDragOver(date, ev)}
      onDragLeave={() => ctx.dayDragLeave(date)}
      onDrop={(ev) => ctx.dayDrop(date, ev)}
      className={cn(
        'flex items-start gap-2 rounded-md border p-1.5 transition-colors',
        overDate === date
          ? 'border-[hsl(var(--primary))]/70 bg-[hsl(var(--primary))]/10'
          : hol
            ? 'pg-hol-hatch border-border bg-white/[0.015]'
            : 'border-border bg-card/40',
      )}
    >
      <button
        type="button"
        onClick={() => ctx.openDay(date, net)}
        title={`Ava päev: ${day}. ${MONTHS[m0]}`}
        className={cn(
          'flex h-6 shrink-0 items-center justify-center gap-1 rounded px-1.5 text-xs transition-colors',
          isToday
            ? 'bg-[hsl(var(--primary))] font-semibold text-primary-foreground'
            : future
              ? 'font-semibold text-[hsl(var(--vatmode))] hover:bg-[hsl(var(--vatmode))]/15'
              : 'text-muted-foreground hover:bg-accent hover:text-foreground',
        )}
      >
        {day}. {MONTHS[m0].slice(0, 3)}
      </button>
      <div className="flex min-w-0 flex-1 flex-wrap items-center gap-1">
        {hol && (
          <span title={`Riigipüha: ${hol}`} className="text-[10px] italic text-white/70">
            {hol}
          </span>
        )}
        <DayEntries date={date} placed={dayEntries} />
      </div>
      <div className="flex shrink-0 flex-col items-end gap-0.5">
        <span
          className={cn(
            'tabular-nums text-xs font-medium',
            net >= 0 ? 'text-[hsl(var(--income))]' : 'text-[hsl(var(--cost))]',
          )}
        >
          {net >= 0 ? '+' : '−'}
          {formatEur(Math.abs(net))}
        </span>
        {/* The opening → closing pair is the running BANK balance (cash truth) —
            hidden under the accrual basis, like every other saldo affordance. */}
        {!accrual && (
          <span className="flex items-center gap-1 tabular-nums text-[10px] text-muted-foreground">
            {formatEurCompact(opening)}
            <span aria-hidden className="opacity-60">
              →
            </span>
            <span className={cn('font-medium', closing < 0 ? 'text-[hsl(var(--cost))]' : 'text-foreground/80')}>
              {formatEurCompact(closing)}
            </span>
          </span>
        )}
      </div>
    </div>
  );
}

/** Päev — a chronological agenda: each month's days that carry entries or a holiday. */
export function DayAgenda() {
  const { model } = useCalendar();
  return (
    <>
      {model.months.map((m) => {
        const prefix = `${m.y}-${pad2(m.m0 + 1)}`;
        const dates = new Set<string>();
        // entriesByDate only ever holds non-empty lists (built by push), so a key's
        // mere presence means the day carries entries.
        for (const date of model.entriesByDate.keys()) {
          if (date.startsWith(prefix)) dates.add(date);
        }
        for (const h of EE_HOLIDAYS_2026) {
          if (h.date.startsWith(prefix) && h.date >= model.firstDate && h.date <= model.lastDate) dates.add(h.date);
        }
        const ordered = Array.from(dates).sort();
        return (
          <section key={`${m.y}-${m.m0}`} className="mb-4">
            <MonthHeader y={m.y} m0={m.m0} />
            {ordered.length === 0 ? (
              <div className="px-1 py-2 text-xs text-muted-foreground">— selles kuus kirjeid pole —</div>
            ) : (
              <div className="space-y-1">
                {ordered.map((date) => (
                  <AgendaRow key={date} date={date} />
                ))}
              </div>
            )}
          </section>
        );
      })}
    </>
  );
}
